import { Box, createStyles, Group, Stack, Tabs } from '@mantine/core';
import { useApiListPolicies } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import TabsList from '@repo/common/components/TabsList';
import { UserRole } from '@repo/common/enums/UserRole';
import { Button } from '@repo/foundations';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuthUser, usePolicyEnabled } from '../../api';
import { EmptyState } from '../../components/EmptyState';
import { FeatureGuard } from '../../components/FeatureGuard/FeatureGuard';
import { FullWidthLoadingSpinner } from '../../components/LoadingSpinner';
import { PoliciesOverviewTabController } from '../../features/Overview/PoliciesOverviewTabController';
import type { MetricCardType } from '../../features/Overview/types';
import { useFeatureFlags } from '../../utils/featureFlags';
import { useListPageTabNavigate } from '../../utils/hook/useTabNavigate';
import { useStyles as catalogStyles } from '../TeamCatalogPage/TeamCatalogPage.styles';
import PoliciesList from './PoliciesList';
import CreatePolicyModal from './components/CreatePolicyModal';
import { PoliciesBanner } from './components/PoliciesBanner';

type PoliciesPageTab = 'policies' | 'overview';

const METRIC_FILTERS: Partial<Record<MetricCardType, any>> = {
	Normal: [{ value: false, operator: 'is', filterType: 'policy_status' }],
	Error: [{ value: true, operator: 'is', filterType: 'policy_status' }],
};

const useStyles = createStyles((theme) => ({
	emptyRoot: {
		padding: theme.spacing.xl,
		gap: theme.spacing.xl,
	},
	emptySpace: {
		width: theme.other.width.md,
		paddingTop: theme.spacing['3xl'],
		paddingBottom: theme.spacing['3xl'],
	},
	emptyStateContainer: {
		borderRadius: theme.other.borderRadius.md,
		border: `1px solid ${theme.other.getColor('border/secondary/default')}`,
		background: theme.other.getColor('surface/primary/default'),
	},
}));

function PoliciesPage() {
	const { user, workspace } = useAuthUser();
	const navigate = useNavigate();
	const { data: policies, isLoading } = useApiListPolicies({});
	const policiesEnabled = usePolicyEnabled();
	const navigateToTab = useListPageTabNavigate();
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const { classes } = useStyles();
	const { dataScanning } = useFeatureFlags();
	const [showBanner, setShowBanner] = useState<boolean>(dataScanning);

	const [activeTab, setActiveTab] = useState<PoliciesPageTab>(() => {
		const storedTab = localStorage.getItem('policies-tab');
		const urlParams = new URLSearchParams(window.location.search);
		// If there are filters in the URL, default to policies tab
		if (urlParams.has('filters')) {
			return 'policies';
		}
		// Otherwise use stored tab or default to policies
		return (storedTab as PoliciesPageTab) || 'policies';
	});

	const handleTabChange = (value: string): void => {
		if (value === 'policies' || value === 'overview') {
			setActiveTab(value);
			localStorage.setItem('policies-tab', value);
			// Only navigate if we're not already on the policies page with filters
			if (
				!(value === 'policies' && window.location.search.includes('filters'))
			) {
				navigateToTab(value);
			}
		}
		trackEvent(
			'policies/tabs/click',
			{
				tab: value,
			},
			user,
			workspace!
		);
	};

	const tabs = useMemo(
		() => [
			{ value: 'overview', label: 'Overview' },
			{ value: 'policies', label: 'Policies' },
		],
		[]
	);

	const { classes: catalogClasses } = catalogStyles();

	const handleSeeHowItWorks = useCallback(() => {
		// TODO: Implement "See how it works" functionality
		trackEvent('policies/banner/see_how_it_works/click', {}, user, workspace!);
	}, [user, workspace]);

	const handleBannerCreatePolicy = useCallback(() => {
		setModalOpen(true);
		trackEvent('policies/banner/create_policy/click', {}, user, workspace!);
	}, [setModalOpen, user, workspace]);

	const handleBannerDismiss = useCallback(() => {
		setShowBanner(false);
		trackEvent('policies/banner/dismiss/click', {}, user, workspace!);
	}, [user, workspace]);

	const learnMoreAction = useMemo(
		() => (
			<Group>
				<Button
					component="a"
					// TODO[tan-policies]: update this link
					href="https://docs.secoda.com/features/policies#how-to-create-policies"
					target="_blank"
					rel="noreferrer noopener"
					size="md"
					leftIconName="externalLink"
				>
					Learn more
				</Button>
				<Button variant="primary" onClick={() => setModalOpen(true)}>
					Create policy
				</Button>
			</Group>
		),
		[setModalOpen]
	);

	const handleMetricCardClick = useCallback(
		(metricType: MetricCardType) => {
			const filters = METRIC_FILTERS[metricType];
			if (!filters) return;

			navigate(
				`/policies?filters=${encodeURIComponent(JSON.stringify(filters))}`
			);
			setActiveTab('policies');
			trackEvent(
				'policies/overview/metric_card/click',
				{
					metric_type: metricType,
				},
				user,
				workspace!
			);
		},
		[navigate, setActiveTab, user, workspace]
	);

	if (policies && policies?.results.length === 0) {
		if (isLoading) {
			return <FullWidthLoadingSpinner />;
		}

		return (
			<Stack className={classes.emptyRoot}>
				{showBanner && (
					<PoliciesBanner
						onCreatePolicy={handleBannerCreatePolicy}
						onSeeHowItWorks={handleSeeHowItWorks}
						onDismiss={handleBannerDismiss}
					/>
				)}
				<Box className={classes.emptyStateContainer}>
					<EmptyState
						className={classes.emptySpace}
						// TODO[tan-policies]: empty state for policies
						illustrationName="policies"
						title="Enable policies to govern your data effectively"
						description="Activate policies to help monitor, validate, and enforce data governance."
						includeGoBack={false}
						stateHeight="80vh"
						size="lg"
						withActions={learnMoreAction}
					/>
				</Box>
				{modalOpen && <CreatePolicyModal onClose={() => setModalOpen(false)} />}
			</Stack>
		);
	}

	return (
		<FeatureGuard
			v1AllowedRoles={[
				UserRole.ADMIN,
				UserRole.EDITOR,
				UserRole.VIEWER,
				UserRole.GUEST,
			]}
			v2Permission="Policies.Read"
			isFeatureIncludedInPlan={policiesEnabled}
			isAlwaysIncludedFeature={false}
			description="Create policies to ensure compliance and security across your data stack."
			featureName="Policies"
		>
			<Stack className={catalogClasses.featureWrapper} spacing={0}>
				<Tabs
					color="fill/brand/default"
					value={activeTab}
					onTabChange={handleTabChange}
					keepMounted={false}
				>
					<TabsList tabs={tabs} />
					<Tabs.Panel value="overview" pt="sm">
						<PoliciesOverviewTabController
							onMetricCardClick={handleMetricCardClick}
						/>
					</Tabs.Panel>
					<Tabs.Panel value="policies" pt="sm">
						<PoliciesList />
					</Tabs.Panel>
				</Tabs>
			</Stack>
		</FeatureGuard>
	);
}

export default PoliciesPage;
